import React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from '@material-ui/icons';
import { red, white } from '@avangard/ui/colors';
import { breakpoints } from '@avangard/ui/utils';

import { STEPS_ORDER } from '@modules/students/requests';

import type { StudentOnlineApplicationStep } from '@modules/types/graphql';

type StudentOnlineApplicationStepsProps = {
    currentStep: StudentOnlineApplicationStep;
    allStepsPassed: boolean;
};

const checkIcon = <CheckCircle style={{ marginTop: -5, color: red[100], fontSize: 34 }} />;

const StudentOnlineApplicationSteps = (
    props: StudentOnlineApplicationStepsProps,
): React.ReactElement => {
    const { currentStep, allStepsPassed } = props;

    const { t } = useTranslation(['students']);

    const getAlign = (idx: number) => {
        if (idx === 0) {
            return 'left';
        }

        if (idx === STEPS_ORDER.length - 1) {
            return 'right';
        }

        return 'center';
    };
    return (
        <Root>
            {STEPS_ORDER.map((step, idx) => {
                const stepIdx = STEPS_ORDER.findIndex(stepItem => stepItem === currentStep);
                const passed = allStepsPassed || stepIdx > idx;

                const numberContent = passed ? checkIcon : idx + 1;

                return (
                    <Step
                        key={idx}
                        passed={passed}
                        current={allStepsPassed || currentStep === step}
                        align={getAlign(idx)}
                    >
                        <StepNumber>{numberContent}</StepNumber>

                        <StepDescription>
                            {t('students:blocks.online_application_steps_short.' + step)}
                        </StepDescription>
                    </Step>
                );
            })}
        </Root>
    );
};

const Root = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    position: relative;

    width: 100%;
    margin-top: 28px;

    &:after {
        content: '';
        position: absolute;
        top: 25%;
        left: 0;
        width: 100%;
        height: 1px;
        background-color: ${red[100]};
    }

    ${breakpoints.down(520)} {
        min-width: 470px;
    }
`;

const StepNumber = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    position: relative;

    width: 28px;
    height: 28px;

    font-size: 1.4rem;
    font-weight: 600;

    background-color: ${white[100]};

    border: 2px solid rgba(0, 0, 0, 0.23);
    border-radius: 50%;
`;

const StepDescription = styled.div`
    width: 100%;
    margin-top: 8px;

    font-size: 1.4rem;
    font-weight: 600;
`;

const Step = styled.div<{ current: boolean; passed: boolean; align: 'left' | 'center' | 'right' }>`
    display: flex;
    flex-wrap: wrap;
    position: relative;
    color: rgba(0, 0, 0, 0.38);
    z-index: 2;

    ${p =>
        p.current &&
        css`
            color: rgba(0, 0, 0, 0.87);

            ${StepNumber} {
                color: ${red[100]};
                border-color: ${red[100]};
            }
        `}

    ${p =>
        p.passed &&
        css`
            color: rgba(0, 0, 0, 0.54);
        `}

    ${p =>
        p.align === 'left' &&
        css`
            justify-content: flex-start;
            text-align: left;
        `}

    ${p =>
        p.align === 'center' &&
        css`
            justify-content: center;
            text-align: center;
        `}

    ${p =>
        p.align === 'right' &&
        css`
            justify-content: flex-end;
            text-align: right;
        `}
`;

export { StudentOnlineApplicationSteps };
